/**
 * <product-offers form-id="product-form-<productId>-<sectionId>" max-select="1">
 *
 * Adds the selected offers to the cart alongside the main product in one /cart/add.js
 * call, mirroring ProductForm._onSubmit in assets/theme.js. Idle when nothing is selected.
 *
 * `max-select` is how many of this block's cards can be held at once, mirroring the backing
 * automatic discount (see custom.compatible_discounts_max in snippets/product-offers.liquid).
 * At one the cards are radios and the browser enforces it; above one they are checkboxes and
 * the cap is enforced here. It is per block, not per form: a phone can discount three
 * accessories and still give only one free gift.
 *
 * A PDP can carry several offer blocks against the same form (discounted accessories and
 * free gifts are separate blocks), so the first element of a form's group submits for all
 * of them — one add call, one cart:change — and the rest stay out of the way.
 *
 * Wrapped in an IIFE because the snippet emits this file once per selectable block, so it
 * can be evaluated more than once on a page.
 */
(() => {
  class ProductOffers extends HTMLElement {
    constructor() {
      super();
      this._onDocumentSubmit = this._onDocumentSubmit.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
      this._submitting = false;
      // Element references, not ids: <product-rerender> rebuilds these inputs with the
      // same ids, and a stale id would make the first click after a re-render deselect.
      this._selected = null;
      // Checked inputs oldest first, so the cap knows which pick to give up.
      this._checkedOrder = [];
    }

    connectedCallback() {
      // This block is never inside the form, so document is the nearest ancestor to listen on, and
      // capture is what puts us ahead of ProductForm: listeners on the target fire in registration
      // order, so binding to the form risks it adding the product first and this adding it again.
      // Matched by id at dispatch, so a rerender that swaps the form rebinds nothing.
      document.addEventListener('submit', this._onDocumentSubmit, true);
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);
      this._checkedOrder = this.selectedOffers;
      this._selected = this._checkedOrder[0] || null;
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this._onDocumentSubmit, true);
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onChange);
    }

    get formId() {
      return this.getAttribute('form-id');
    }

    get maxSelect() {
      const max = Number(this.getAttribute('max-select'));
      return Number.isFinite(max) && max > 0 ? max : 1;
    }

    get offerInputs() {
      return Array.from(this.querySelectorAll('input.product-offers__input'));
    }

    // In DOM order, which is also pick order on a freshly rendered page.
    get selectedOffers() {
      return this.offerInputs.filter((input) => input.checked && Number(input.dataset.variantId) > 0);
    }

    clearSelection() {
      this.offerInputs.forEach((input) => {
        input.checked = false;
      });
      this._selected = null;
      this._checkedOrder = [];
    }

    // Every offer block bound to the same product form, in DOM order.
    get group() {
      const formId = this.formId;
      return Array.from(document.querySelectorAll('product-offers')).filter(
        (element) => element.getAttribute('form-id') === formId
      );
    }

    _isOfferInput(node) {
      return node instanceof HTMLInputElement && node.classList.contains('product-offers__input');
    }

    // Radios can't be cleared natively, so clicking the selected card deselects it. "click"
    // fires before "change", so _selected still holds the previous selection here. Checkboxes
    // are left alone: they clear themselves, and unchecking one here would undo the native
    // toggle and make the card look stuck.
    _onClick(event) {
      if (!this._isOfferInput(event.target)) return;
      if (event.target.type !== 'radio') return;

      if (this._selected === event.target) {
        event.target.checked = false;
        this._selected = null;
        this._checkedOrder = [];
      }
    }

    _onChange(event) {
      const input = event.target;
      if (!this._isOfferInput(input)) return;

      if (input.type === 'radio') {
        // Checking one unchecks its group, so there is never anything to give up.
        this._selected = input.checked ? input : null;
        this._checkedOrder = input.checked ? [input] : [];
        return;
      }

      // Rebuilt rather than spliced: a re-render can leave detached inputs in here, and a radio
      // block sharing this element would too.
      this._checkedOrder = this._checkedOrder.filter((checked) => checked !== input && checked.checked);

      if (input.checked) {
        this._checkedOrder.push(input);
      }

      // Over the cap the oldest pick gives way, rather than the click being refused: a card that
      // silently declines to tick reads as broken, and the customer cannot tell which of their
      // earlier picks is in the way.
      while (this._checkedOrder.length > this.maxSelect) {
        const oldest = this._checkedOrder.shift();
        if (oldest) oldest.checked = false;
      }

      this._selected = this._checkedOrder[this._checkedOrder.length - 1] || null;
    }

    _onDocumentSubmit(event) {
      const formId = this.formId;
      const form = event.target;

      if (!formId || !(form instanceof HTMLFormElement)) return;

      // `form.id` returns the input *named* "id" on a product form, not the attribute.
      if (form.getAttribute('id') !== formId) return;

      if (typeof Shopify === 'undefined' || !Shopify.routes || !Shopify.routes.root) return;

      const group = this.group;
      if (group[0] !== this) return; // A sibling block submits for the whole group.

      const selected = group.flatMap((element) => element.selectedOffers);
      if (selected.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation(); // ProductForm's own submit handler must not run

      if (this._submitting) return;

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      this._addToCart(form, group, selected);
    }

    async _addToCart(form, group, selected) {
      const submitButtons = Array.from(form.elements).filter((element) => element.type === 'submit');
      submitButtons.forEach((submitButton) => submitButton.setAttribute('aria-busy', 'true'));
      this._submitting = true;

      try {
        const formData = new FormData(form);
        const mainVariantId = Number(formData.get('id'));
        if (!mainVariantId) return;

        const mainItem = { id: mainVariantId, quantity: this._mainQuantity(formData) };

        const properties = this._lineItemProperties(formData);
        if (Object.keys(properties).length > 0) {
          mainItem.properties = properties;
        }

        const sellingPlan = formData.get('selling_plan');
        if (typeof sellingPlan === 'string' && sellingPlan !== '') {
          mainItem.selling_plan = sellingPlan;
        }

        const items = [mainItem, ...this._offerItems(selected)];

        const sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest', // Needed for Shopify to check inventory
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: `${Shopify.routes.root}variants/${mainVariantId}`,
          }),
        });

        const responseJson = await response.json();

        if (!response.ok) {
          this._dispatchError(form, responseJson && responseJson['description']);
          return;
        }

        group.forEach((element) => element.clearSelection());

        if (
          window.themeVariables?.settings?.cartType === 'page' ||
          window.themeVariables?.settings?.pageType === 'cart'
        ) {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent['sections'] = responseJson['sections'];

        form.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
              cart: cartContent,
            },
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent,
            },
          })
        );
      } catch (error) {
        this._dispatchError(form, error && error.message);
      } finally {
        submitButtons.forEach((submitButton) => submitButton.removeAttribute('aria-busy'));
        this._submitting = false;
      }
    }

    // Two blocks can offer the same product, and /cart/add.js would then process it twice.
    // Merged into one line so the quantity is what the customer actually picked.
    _offerItems(selected) {
      const quantities = new Map();

      selected.forEach((input) => {
        const id = Number(input.dataset.variantId);
        if (!id) return;
        quantities.set(id, (quantities.get(id) || 0) + 1);
      });

      return Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
    }

    _mainQuantity(formData) {
      const quantity = parseInt(formData.get('quantity'), 10);
      return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
    }

    _lineItemProperties(formData) {
      const properties = {};
      formData.forEach((value, key) => {
        if (typeof value !== 'string') return;
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) {
          properties[match[1]] = value;
        }
      });
      return properties;
    }

    _dispatchError(form, message) {
      form.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: message },
        })
      );
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  if (!customElements.get('product-offers')) {
    customElements.define('product-offers', ProductOffers);
  }
})();
